import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

interface Project {
  requests: any;
  id: string;
  title: string;
  institution: string;
  description: string;
  requirements: string;
  budget: number;
  deadline: string;
  status: 'pending' | 'active' | 'in-progress' | 'completed' | 'cancelled';
  services: any[];
  files: any[];
  completed_work: any[];
  client_id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  freelancer_id?: string;
  freelancer_name?: string;
  assigned_at?: string;
  completed_at?: string;
  payment_status: string;
  created_at: string;
}

const ProjectDetails: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasRequested, setHasRequested] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  useEffect(() => {
    const loadUserData = () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
          navigate('/signin');
          return;
        }

        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error loading user data:', error);
        navigate('/signin');
      }
    };

    loadUserData();
  }, [navigate]);

  const checkIfRequested = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `http://localhost:5000/api/projects/freelancer/requests`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        const hasRequest = response.data.data.requests.some(
          (req: any) => req.project_id === projectId && req.status === 'pending'
        );
        setHasRequested(hasRequest);
      }
    } catch (error) {
      console.error('Error checking request status:', error);
    }
  }, [projectId]);

  const loadProjectDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `http://localhost:5000/api/projects/${projectId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setProject(response.data.data.project);
        
        // Check if freelancer has requested this project
        if (user?.role === 'freelancer') {
          await checkIfRequested();
        }
      }
    } catch (error) {
      console.error('Error loading project details:', error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, user, checkIfRequested]);

  useEffect(() => {
    if (user && projectId) {
      loadProjectDetails();
    }
  }, [user, projectId, loadProjectDetails]);

  const handleRequestProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `http://localhost:5000/api/projects/${projectId}/request`,
        {
          message: `I'm interested in working on this project. I have relevant experience in this field.`
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        alert('Project request submitted successfully!');
        setHasRequested(true);
      }
    } catch (error: any) {
      console.error('Error requesting project:', error);
      alert(error.response?.data?.message || 'Error submitting request');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleUploadCompletedWork = async () => {
    if (!uploadedFiles.length) {
      alert('Please select files to upload');
      return;
    }

    try {
      setIsUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      uploadedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(
        `http://localhost:5000/api/projects/${projectId}/upload-completed`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        alert('Completed work uploaded successfully!');
        setUploadedFiles([]);
        loadProjectDetails();
      }
    } catch (error: any) {
      console.error('Error uploading completed work:', error);
      alert(error.response?.data?.message || 'Error uploading files');
    } finally {
      setIsUploading(false);
    }
  };

  const handleMarkComplete = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to mark this project as complete? This will release payment to the freelancer.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `http://localhost:5000/api/projects/${projectId}/complete`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        alert('Project marked as complete! Payment has been released to the freelancer.');
        loadProjectDetails();
      }
    } catch (error: any) {
      console.error('Error completing project:', error);
      alert(error.response?.data?.message || 'Error completing project');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'in-progress': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading project details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h2>
            <p className="text-gray-600 mb-6">The requested project could not be found.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-primary"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isClient = user?.role === 'client' && user?.id === project.client_id;
  const isFreelancer = user?.role === 'freelancer' && user?.id === project.freelancer_id;
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          
          <div className="navbar-actions">
            <div className="user-info">
              <div className="user-avatar">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="user-details">
                <span className="user-name">{user?.username?.toUpperCase()}</span>
                <div className="user-meta">
                  <span className={`role-badge role-${user?.role}`}>{user?.role}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900">{project.title}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
              </div>
              <p className="text-gray-600">
                {project.institution} • Posted by {project.client_name} on {formatDate(project.created_at)}
              </p>
            </div>
            <button 
              onClick={() => navigate(-1)}
              className="btn btn-secondary"
            >
              ← Back
            </button>
          </div>

          {/* Budget and Deadline */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600 mb-1">Budget</div>
              <div className="text-2xl font-bold text-green-600">₦{project.budget.toLocaleString()}</div>
              <div className="text-sm text-gray-600 mt-1">Payment Status: {project.payment_status}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600 mb-1">Deadline</div>
              <div className="text-xl font-bold text-gray-900">{formatDate(project.deadline)}</div>
              <div className="text-sm text-gray-600 mt-1">
                {project.assigned_at ? `Assigned on ${formatDate(project.assigned_at)}` : 'Not assigned yet'}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600 mb-1">Freelancer</div>
              <div className="text-xl font-bold text-gray-900">
                {project.freelancer_name || 'Not assigned'}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {project.completed_at ? `Completed on ${formatDate(project.completed_at)}` : 'In progress'}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            {['overview', 'services', 'files', 'completed-work', 'messages'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Project Description</h3>
                <p className="text-gray-700 whitespace-pre-line">{project.description}</p>
              </div>
              
              {project.requirements && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Additional Requirements</h3>
                  <p className="text-gray-700 whitespace-pre-line">{project.requirements}</p>
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Client Name</div>
                    <div className="font-medium">{project.client_name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Client Email</div>
                    <div className="font-medium">{project.client_email}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Client Phone</div>
                    <div className="font-medium">{project.client_phone}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Institution</div>
                    <div className="font-medium">{project.institution}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Services</h3>
              <div className="space-y-4">
                {project.services.map((service, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-medium text-gray-900">{service.subServiceName}</h4>
                        <p className="text-sm text-gray-600">Quantity: {service.quantity}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600">
                          ₦{service.totalPrice.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-600">
                          ₦{service.unitPrice.toLocaleString()} × {service.quantity}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      Delivery Time: {service.deliveryTime}
                    </div>
                  </div>
                ))}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total Budget:</span>
                    <span className="text-green-600">₦{project.budget.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'files' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Files</h3>
              {project.files && project.files.length > 0 ? (
                <div className="space-y-3">
                  {project.files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <span className="text-blue-600">📄</span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{file.originalName}</div>
                          <div className="text-sm text-gray-600">
                            {formatFileSize(file.size)} • {file.mimeType?.split('/')[1]?.toUpperCase() || 'FILE'}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => window.open(`http://localhost:5000${file.filePath}`, '_blank')}
                        className="btn btn-secondary btn-sm"
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No files uploaded for this project.</p>
              )}
            </div>
          )}

          {activeTab === 'completed-work' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Completed Work</h3>
              
              {isFreelancer && project.status === 'active' && (
                <div className="mb-6 bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Upload Completed Work</h4>
                  <div className="space-y-3">
                    <div className="form-group">
                      <label htmlFor="completedWorkFiles" className="block text-sm font-medium text-gray-700 mb-2">
                        Select files to upload
                      </label>
                      <input
                        id="completedWorkFiles"
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                        className="form-input"
                        accept=".pdf,.doc,.docx,.ppt,.pptx,.zip,.rar"
                        title="Select completed work files to upload"
                        placeholder="Choose files..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Accepted formats: PDF, DOC, DOCX, PPT, PPTX, ZIP, RAR
                      </p>
                    </div>
                    {uploadedFiles.length > 0 && (
                      <div className="space-y-2">
                        {uploadedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between text-sm">
                            <span>{file.name}</span>
                            <button
                              onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== index))}
                              className="text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={handleUploadCompletedWork}
                      disabled={isUploading || uploadedFiles.length === 0}
                      className="btn btn-primary"
                    >
                      {isUploading ? 'Uploading...' : 'Upload Completed Work'}
                    </button>
                  </div>
                </div>
              )}

              {project.completed_work && project.completed_work.length > 0 ? (
                <div className="space-y-3">
                  {project.completed_work.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <span className="text-green-600">✓</span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{file.originalName}</div>
                          <div className="text-sm text-gray-600">
                            Uploaded {formatDate(file.uploadedAt)} • {formatFileSize(file.size)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => window.open(`http://localhost:5000${file.filePath}`, '_blank')}
                        className="btn btn-secondary btn-sm"
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No completed work uploaded yet.</p>
              )}
            </div>
          )}

          {activeTab === 'messages' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Messages</h3>
              <p className="text-gray-600 mb-4">
                Messages are available in the dedicated messaging section.
              </p>
              <button
                onClick={() => navigate(`/messages/${projectId}`)}
                className="btn btn-primary"
              >
                Go to Messages
              </button>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex flex-wrap gap-3">
          {isFreelancer && project.status === 'active' && (
            <button
              onClick={() => setActiveTab('completed-work')}
              className="btn btn-primary"
            >
              Upload Completed Work
            </button>
          )}

          {isClient && project.status === 'in-progress' && project.completed_work?.length > 0 && (
            <button
              onClick={handleMarkComplete}
              className="btn btn-success"
            >
              Mark as Complete & Release Payment
            </button>
          )}

          {user?.role === 'freelancer' && !isFreelancer && project.status === 'pending' && (
            <button
              onClick={handleRequestProject}
              disabled={hasRequested}
              className={`btn ${hasRequested ? 'btn-secondary' : 'btn-primary'}`}
            >
              {hasRequested ? 'Request Pending' : 'Request to Work on Project'}
            </button>
          )}

          {isAdminOrManager && (
            <>
              <button
                onClick={() => navigate(`/admin/projects/${projectId}/requests`)}
                className="btn btn-secondary"
              >
                View Requests
              </button>
              <button
                onClick={() => navigate(`/admin/projects/${projectId}/assign`)}
                className="btn btn-primary"
              >
                Assign Project
              </button>
            </>
          )}

          <button
            onClick={() => navigate(`/messages/${projectId}`)}
            className="btn btn-secondary"
          >
            Message {isClient ? 'Freelancer' : 'Client'}
          </button>

          {isClient && project.status === 'pending' && (
            <button
              onClick={() => navigate(`/client/projects/${projectId}/requests`)}
              className="btn btn-secondary"
            >
              View Requests ({project.requests?.length || 0})
            </button>
          )}

          {isClient && project.payment_status === 'pending' && (
            <button
              onClick={() => navigate(`/client/projects/${projectId}/payment`)}
              className="btn btn-primary"
            >
              Make Payment
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;